/**
 * The helpable-at-live-event impression effect (Track 2 Phase A, spec 3.5) -
 * the conditional sibling of `useLiveImpression`. It fires `live_surface_displayed`
 * once per appearance for a helpable surface, but ONLY while `when` holds (the
 * chip is actually visible / the section has resolved) and it stamps the extra
 * `helpable_count` the plain live impression never carries.
 *
 * Shared by all three "displayed" helpable surfaces so the once-per-appearance +
 * strict-mode-safe guard lives in ONE tested place:
 *   - `for_you_section`  : `when` = section resolved, count = total matches.
 *   - `pill_help_chip`   : `when` = helpableCount > 0, count = the pill's count.
 *   - `feed_help_chip`   : `when` = chips-in-loaded-feed > 0, count = that many.
 *
 * Guard: a ref Set keyed by `event_id:appearanceKey` (mirrors `useLiveImpression`,
 * not the bare `appearanceKey`). Re-renders (data resolve, 30s ticks, refetch
 * churn) are no-ops once fired; a new appearance - a phase flip, a new event, a
 * new space visit, OR the live event changing under a caller that keys by a
 * non-event id (Meetverse keys by spaceId) - re-arms and fires again. The ref
 * never clears on cleanup, so React StrictMode's mount → unmount → remount
 * double-run fires exactly once (same instance, same ref). `when` flipping
 * false → true (the count resolving after mount) fires on the render it clears,
 * never before.
 *
 * useEffect is the sanctioned tool here (house rule: analytics-on-mount IS
 * external-system synchronization).
 */
import { useEffect, useRef } from 'react';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS, type LiveSurface } from '@lib/analytics-events';
import type { HelpableEventContext } from '@lib/analytics/payloads';

/** The three helpable surfaces that emit `live_surface_displayed` (spec 3.5). */
export type HelpableImpressionSurface = Extract<
  LiveSurface,
  'for_you_section' | 'pill_help_chip' | 'feed_help_chip'
>;

export interface UseHelpableSurfaceImpressionParams {
  /** Which helpable surface is being shown (the `surface` payload value). */
  surface: HelpableImpressionSurface;
  /** Event + parent-space context, prebuilt via `buildHelpableEventContext`. */
  eventContext: HelpableEventContext;
  /** The `helpable_count` to stamp (section total / pill count / chips shown). */
  helpableCount: number;
  /** Fire only while true - the surface is actually visible/resolved. */
  when: boolean;
  /** Appearance identity; a new value re-arms the once-per guard. */
  appearanceKey: string;
}

export function useHelpableSurfaceImpression({
  surface,
  eventContext,
  helpableCount,
  when,
  appearanceKey,
}: UseHelpableSurfaceImpressionParams): void {
  const { trackEvent } = useAmplitudeTracking();
  const firedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Bake the event id into the guard key so a caller keyed by a non-event id
    // (Meetverse's spaceId) still re-arms when the space's live event changes.
    // `event_id` can be null (no event) - the '' segment keeps the key stable.
    const firedKey = `${eventContext.event_id ?? ''}:${appearanceKey}`;
    if (!when || firedKeysRef.current.has(firedKey)) {
      return;
    }
    firedKeysRef.current.add(firedKey);
    trackEvent(ANALYTICS_EVENTS.live_surface_displayed, {
      surface,
      helpable_count: helpableCount,
      ...eventContext,
    });
  }, [when, appearanceKey, surface, helpableCount, eventContext, trackEvent]);
}
